import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { pricingTiers, extraOptions } from '../mock';
import { useTranslation } from '../LanguageContext';

export default function PricingPage() {
  const { t, price } = useTranslation();

  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
      <div className="text-center mb-14">
        <div className="eyebrow mb-5">{t('pricing.eyebrow')}</div>
        <h1 className="font-serif text-[2.6rem] md:text-[4rem] leading-[1.05] mb-5">
          {t('pricing.title1')} <span className="italic-serif">{t('pricing.title2')}</span> {t('pricing.title3')}
        </h1>
        <p className="text-[color:var(--ink-soft)] max-w-xl mx-auto text-lg">
          {t('pricing.subtitle')}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-20">
        {pricingTiers.map((tier) => (
          <div
            key={tier.id}
            className={`relative rounded-3xl p-8 transition ${tier.highlight ? 'bg-[color:var(--burgundy)] text-[#f8ede0] shadow-2xl md:-translate-y-2' : 'bg-white/60 border border-[color:var(--border)]'}`}
          >
            {tier.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs bg-[#f8ede0] text-[color:var(--burgundy)] tracking-widest">
                {t('pricing.mostChosen')}
              </div>
            )}
            <div className={`eyebrow mb-2 ${tier.highlight ? '!text-[#f8ede0]/80' : ''}`}>{t(`tier.${tier.id}.tagline`)}</div>
            <h3 className="font-serif text-3xl mb-4">{tier.name}</h3>
            <div className="mb-6">
              <span className="font-serif text-5xl">{price(tier.price)}</span>
              <span className={`ml-2 text-sm ${tier.highlight ? 'text-[#f8ede0]/70' : 'text-[color:var(--ink-soft)]'}`}>{t('pricing.oneTime')}</span>
            </div>
            <ul className="space-y-3">
              {tier.features.map((_, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <Check size={16} className={`mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-[#f8ede0]' : 'text-[color:var(--burgundy)]'}`} />
                  <span>{t(`tier.${tier.id}.f${i + 1}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="eyebrow mb-3">{t('pricing.extras.eyebrow')}</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-3">{t('pricing.extras.title1')} <span className="italic-serif">{t('pricing.extras.title2')}</span>.</h2>
          <p className="text-[color:var(--ink-soft)]">{t('pricing.extras.hint')} <b className="text-[color:var(--burgundy)]">{price(0.5)}</b>. {t('pricing.extras.hint2')}</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-14">
          {extraOptions.map((o) => (
            <div key={o.id} className="flex items-center justify-between p-4 rounded-xl bg-white/50 border border-[color:var(--border)]">
              <div className="flex items-center gap-3">
                <Sparkles size={14} className="text-[color:var(--burgundy)]" />
                <span className="text-sm text-[color:var(--ink)]">{t(`${o.id}.label`)}</span>
              </div>
              <span className="text-sm text-[color:var(--burgundy)] font-medium">+{price(o.price)}</span>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/crea" className="btn-primary inline-flex items-center gap-2">
            <Sparkles size={16} /> {t('home.cta.create')}
          </Link>
          <p className="text-sm text-[color:var(--ink-soft)] italic mt-4">{t('pricing.freeHint')}</p>
        </div>
      </div>
    </section>
  );
}
